import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { InputAdornment, TextField } from "@mui/material";
import { withSuperAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import { formatGBP2 } from "../../../components/Features";
import billingStyles from "../../../components/billing.module.css";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { saGet, saPut, TableSkeleton, Toast, useToast } from "../shared";
import styles from "../superadmin.module.css";

const NUMBER_KEYS = ["basic_plan_price", "trial_days", "invoice_lead_days", "grace_period_days"];

// Platform billing settings: Basic price, billing timeline and invoice issuer details
const BillingSettings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, showMessage, closeToast] = useToast();

  const { websiteData } = useWebsite();
  useSiteBranding("Billing settings", websiteData);

  useEffect(() => {
    saGet("settings").then((response) => {
      if (response.data.status === "SUCCESS") setForm(response.data.data);
    });
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    NUMBER_KEYS.forEach((k) => { payload[k] = Number(payload[k]); });
    saPut("settings", payload).then((response) => {
      setSaving(false);
      showMessage(response.data.message, response.data.status === "SUCCESS" ? "success" : "error");
      if (response.data.status === "SUCCESS") setForm(response.data.data);
    }).catch(() => { setSaving(false); showMessage("Could not save settings.", "error"); });
  };

  // Worked example of the timeline for a company signing up today
  const trialEnd = dayjs().add(Number(form?.trial_days || 0), "day");
  const billDate = trialEnd.add(1, "month");
  const invoiceDate = billDate.subtract(Number(form?.invoice_lead_days || 0), "day");
  const suspendDate = billDate.add(Number(form?.grace_period_days || 0) + 1, "day");

  return (<div className={`${styles.Page}`}>
    <PageHeader title="Billing Settings" trail={[{ label: "Billing", to: "/super-admin/invoices" }]} />

    {!form && <TableSkeleton rows={6} />}

    {form && (<form onSubmit={save}>
      <div className={`${styles.GridHalf}`}>
        <div className={`${styles.Panel}`}>
          <p className={`${styles.PanelTitle}`} style={{ marginBottom: 10 }}>Pricing</p>
          <p className={`${styles.GroupDesc}`}>The single Basic plan every company is on. Add-on prices are set per add-on on <Link to={"/super-admin/addons"}>Add-ons &amp; Pricing</Link>.</p>
          <TextField label="Basic plan, per month" type="number" value={form.basic_plan_price ?? ""} onChange={set("basic_plan_price")} fullWidth size="small" inputProps={{ min: 0, step: "0.01" }} InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }} helperText="New price applies from each company's next invoice." />
        </div>

        <div className={`${styles.Panel}`}>
          <p className={`${styles.PanelTitle}`} style={{ marginBottom: 10 }}>Billing timeline</p>
          <p className={`${styles.GroupDesc}`}>Invoices are generated and emailed automatically every day at 06:00.</p>
          <div className={`${styles.FormGrid}`}>
            <TextField label="Free trial (days)" type="number" value={form.trial_days ?? ""} onChange={set("trial_days")} size="small" inputProps={{ min: 0, max: 365 }} />
            <TextField label="Email invoice before billing date (days)" type="number" value={form.invoice_lead_days ?? ""} onChange={set("invoice_lead_days")} size="small" inputProps={{ min: 0, max: 28 }} />
            <TextField label="Grace period after due date (days)" type="number" value={form.grace_period_days ?? ""} onChange={set("grace_period_days")} size="small" inputProps={{ min: 0, max: 90 }} className={`${styles.FormFull}`} />
          </div>
          <div className={`${styles.Example}`}>
            A company signing up today: trial ends <strong>{trialEnd.format("DD MMM")}</strong> → first invoice emailed <strong>{invoiceDate.format("DD MMM")}</strong> → due <strong>{billDate.format("DD MMM")}</strong> → if unpaid, service stops on <strong>{suspendDate.format("DD MMM YYYY")}</strong>. First bill: <strong>{formatGBP2(form.basic_plan_price)}</strong> + add-ons.
          </div>
        </div>
      </div>

      <div className={`${styles.Panel}`}>
        <p className={`${styles.PanelTitle}`} style={{ marginBottom: 10 }}>Invoice issuer</p>
        <p className={`${styles.GroupDesc}`}>Shown on every invoice PDF and billing email. Payment instructions also appear on the company's suspension page.</p>
        <div className={`${styles.FormGrid}`}>
          <TextField label="Company name" value={form.billing_company_name || ""} onChange={set("billing_company_name")} size="small" required />
          <TextField label="Invoice number prefix" value={form.invoice_prefix || ""} onChange={set("invoice_prefix")} size="small" required helperText={`Next numbers look like ${form.invoice_prefix || ""}${dayjs().format("YYYY")}-00001`} />
          <TextField label="Billing email" type="email" value={form.billing_email || ""} onChange={set("billing_email")} size="small" helperText="Shown to companies for billing questions" />
          <TextField label="VAT number" value={form.billing_vat_no || ""} onChange={set("billing_vat_no")} size="small" />
          <TextField label="Address" value={form.billing_address || ""} onChange={set("billing_address")} size="small" multiline minRows={3} className={`${styles.FormFull}`} />
          <TextField label="Payment instructions" value={form.payment_instructions || ""} onChange={set("payment_instructions")} size="small" multiline minRows={4} className={`${styles.FormFull}`} placeholder={"Bank: …\nAccount name: …\nSort code: …\nAccount number: …"} helperText="Companies pay offline; you record payments on the Invoices page." />
        </div>
      </div>

      <div className={`${styles.FormActions}`}>
        <button type="submit" className={`${billingStyles.PrimaryBU}`} disabled={saving}>{saving ? "Saving…" : "Save settings"}</button>
      </div>
    </form>)}

    <Toast toast={toast} onClose={closeToast} />
  </div>);
}

export default withSuperAdminAuth(BillingSettings);
